import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './PaymentsPage.scss';
import UIButton from '@/components/shared/UIButton';
import UIPageTitle from '@/components/shared/UIPageTitle';
import UIIconButton from '@/components/shared/UIIconButton';
import UIAlert from '@/components/shared/UIAlert';
import PaymentsOverviewSection from '@/components/dashboard/Payments/PaymentsOverviewSection';
import PaymentsTransactionsSection from '@/components/dashboard/Payments/PaymentsTransactionsSection/PaymentsTransactionsSection';
import { getFullReportRequest } from '@/api/payments';
import { usePayments } from '@/hooks/usePayments';
import useGetDictionaries from '@/hooks/useGetDictionaries';
import { PaymentDictionariesResponse, PaymentType } from '@/types/payments';

// Тип для фільтрів
interface ReportFilters {
  period?: string;
  propertyId?: string;
  tenantId?: string;
}

// Тип для даних форми додавання платежу
interface AddPaymentData {
  tenantId: string;
  paymentType: string;
  amount: number;
  description?: string;
}

// Тип для алерту
interface Alert {
  id: string;
  variant: 'success' | 'danger' | 'warning' | 'primary';
  message: string;
}

// Константи для localStorage ключів
const STORAGE_KEYS = {
  SELECTED_PROPERTY: 'payments_selected_property',
  SELECTED_TENANT: 'payments_selected_tenant',
  SELECTED_TIME_PERIOD: 'payments_selected_time_period',
} as const;

// Функції для роботи з localStorage
const saveFiltersToStorage = (
  property: string,
  tenant: string,
  timePeriod: string,
) => {
  try {
    localStorage.setItem(STORAGE_KEYS.SELECTED_PROPERTY, property);
    localStorage.setItem(STORAGE_KEYS.SELECTED_TENANT, tenant);
    localStorage.setItem(STORAGE_KEYS.SELECTED_TIME_PERIOD, timePeriod);
  } catch (error) {
    console.warn('Failed to save filters to localStorage:', error);
  }
};

const loadFiltersFromStorage = () => {
  try {
    return {
      property:
        localStorage.getItem(STORAGE_KEYS.SELECTED_PROPERTY) ||
        'all-properties',
      tenant:
        localStorage.getItem(STORAGE_KEYS.SELECTED_TENANT) || 'all-tenants',
      timePeriod:
        localStorage.getItem(STORAGE_KEYS.SELECTED_TIME_PERIOD) || 'all-time',
    };
  } catch (error) {
    console.warn('Failed to load filters from localStorage:', error);
    return {
      property: 'all-properties',
      tenant: 'all-tenants',
      timePeriod: 'all-time',
    };
  }
};

const BackToDashboardButton = () => {
  const navigate = useNavigate();
  return (
    <UIButton
      outline={true}
      variant="primary"
      size="medium"
      onClick={() => navigate('/dashboard')}
    >
      Back to Dashboard
    </UIButton>
  );
};

// Кнопка тепер приймає фільтри
const DownloadReportButton = ({ filters }: { filters: ReportFilters }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleDownload = async () => {
    setIsLoading(true);
    try {
      // Передаємо фільтри в API запит
      const response = await getFullReportRequest(filters);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'full-report.html');
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download report:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <UIIconButton
      variant="primary"
      size="medium"
      iconType="download"
      text="Download Full Report"
      onClick={handleDownload}
      disabled={isLoading}
    />
  );
};

const PaymentsPage = () => {
  // Ініціалізуємо стан з localStorage
  const savedFilters = loadFiltersFromStorage();
  const [selectedProperty, setSelectedProperty] = useState(
    savedFilters.property,
  );
  const [selectedTenant, setSelectedTenant] = useState(savedFilters.tenant);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState(
    savedFilters.timePeriod,
  );

  // Стан для алертів
  const [alerts, setAlerts] = useState<Alert[]>([]);

  const { createPayment } = usePayments();
  const { data: dictionaries } =
    useGetDictionaries<PaymentDictionariesResponse>('payments');

  // Зберігаємо фільтри в localStorage при зміні
  useEffect(() => {
    saveFiltersToStorage(selectedProperty, selectedTenant, selectedTimePeriod);
  }, [selectedProperty, selectedTenant, selectedTimePeriod]);

  // Функції для управління алертами
  const addAlert = (variant: Alert['variant'], message: string) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    setAlerts((prev) => [...prev, { id, variant, message }]);

    // Автоматично видаляємо алерт через 5 секунд
    setTimeout(() => {
      removeAlert(id);
    }, 5000);
  };

  const removeAlert = (id: string) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
  };

  // Обробники для зміни фільтрів
  const handlePropertyChange = (property: string) => {
    setSelectedProperty(property);
  };

  const handleTenantChange = (tenant: string) => {
    setSelectedTenant(tenant);
  };

  const handleTimePeriodChange = (period: string) => {
    setSelectedTimePeriod(period);
  };

  const handleAddPayment = async (paymentData: AddPaymentData) => {
    console.log('Creating payment with data:', paymentData); // DEBUG
    try {
      const response = await createPayment({
        tenantId: parseInt(paymentData.tenantId, 10),
        amount: paymentData.amount,
        type: paymentData.paymentType as PaymentType,
        description: paymentData.description,
      });
      console.log('Payment response:', response); // DEBUG

      if (response) {
        addAlert('success', 'Payment created successfully! An email with payment link has been sent to the tenant.');
        // Тільки при успіху форма закривається автоматично через AddPaymentModal
      }
    } catch (error: any) {
      console.error('Failed to create payment:', error);

      // Обробка різних типів помилок
      let errorMessage = 'Failed to create payment. Please try again.';

      if (error?.response?.data) {
        const errorData = error.response.data;

        // Якщо є validation errors
        if (errorData.errors && typeof errorData.errors === 'object') {
          const validationErrors = Object.entries(errorData.errors)
            .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
            .join('; ');
          errorMessage = `Validation failed: ${validationErrors}`;
        }
        // Якщо є загальне повідомлення
        else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }

      addAlert('danger', errorMessage);
      // При помилці форма НЕ закривається - користувач може виправити дані
      throw error; // Кидаємо помилку далі, щоб AddPaymentModal не закрився
    }
  };

  const currentFilters = {
    propertyId: selectedProperty,
    tenantId: selectedTenant,
    period: selectedTimePeriod,
  };

  return (
    <>
      <UIPageTitle
        title="Payments"
        action={
          <div style={{ display: 'flex', gap: '16px' }}>
            <BackToDashboardButton />
            {/* Передаємо фільтри в кнопку */}
            <DownloadReportButton filters={currentFilters} />
          </div>
        }
      />

      {/* Відображення алертів */}
      {alerts.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            width: '100%',
          }}
        >
          {alerts.map((alert: Alert) => (
            <UIAlert
              key={alert.id}
              variant={alert.variant}
              closable={true}
              onClose={() => removeAlert(alert.id)}
              fullWidth={true}
            >
              {alert.message}
            </UIAlert>
          ))}
        </div>
      )}

      <PaymentsOverviewSection
        onPeriodChange={(period) => console.log('Period changed:', period)}
        onToggleCollapse={(isCollapsed) =>
          console.log('Section collapsed:', isCollapsed)
        }
      />

      {/* Передаємо стан і обробники в дочірній компонент */}
      <PaymentsTransactionsSection
        selectedProperty={selectedProperty}
        selectedTenant={selectedTenant}
        selectedTimePeriod={selectedTimePeriod}
        onPropertyChange={handlePropertyChange}
        onTenantChange={handleTenantChange}
        onTimePeriodChange={handleTimePeriodChange}
        onAddPayment={handleAddPayment}
      />
    </>
  );
};

export default PaymentsPage;
